import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Film, Layers, LogOut, User } from "lucide-react";

/**
 * Mobile shell — persistent bottom-tab layout used by every /m/* route.
 * Two tabs only: Library + Reels. Full-screen video previews.
 * Uses the SAME auth as the desktop app (cookie-based), so users don't
 * need a separate account — but a logged-out visitor is bounced to /m/login.
 */
export default function MobileShell() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      nav("/m/login", { replace: true, state: { from: loc.pathname } });
    }
  }, [user, loading, nav, loc.pathname]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    window.location.href = "/m/login";
  };

  if (loading || !user) {
    return (
      <div className="min-h-[100dvh] bg-stone-950 text-stone-300 flex items-center justify-center">
        <div className="animate-pulse text-xs uppercase tracking-widest">Loading…</div>
      </div>
    );
  }

  const linkCls = (active) =>
    `flex-1 flex flex-col items-center justify-center gap-1 py-3 text-[10px] uppercase tracking-widest ${
      active ? "text-orange-400" : "text-stone-500 hover:text-stone-300"
    }`;

  return (
    <div className="min-h-[100dvh] bg-stone-950 text-stone-100 flex flex-col" data-testid="mobile-shell">
      {/* Header — thin, brand + user pill */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800 sticky top-0 bg-stone-950/95 backdrop-blur z-30">
        <div className="font-display uppercase tracking-wider text-sm">Rodeo <span className="text-orange-400">Cut</span></div>
        <button
          onClick={logout}
          className="text-[10px] text-stone-400 hover:text-stone-200 inline-flex items-center gap-1 uppercase tracking-widest"
          data-testid="mobile-logout"
        >
          <User size={12} /> {user.email?.split("@")[0]?.slice(0, 14) || "you"} · <LogOut size={11} />
        </button>
      </div>

      {/* Route outlet */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-16" data-testid="mobile-content">
        <Outlet />
      </div>

      {/* Bottom tabs */}
      <nav className="fixed bottom-0 left-0 right-0 border-t border-stone-800 bg-stone-950 flex z-40" data-testid="mobile-tabs">
        <NavLink to="/m/library" className={({ isActive }) => linkCls(isActive)} data-testid="mobile-tab-library">
          <Layers size={18} />
          Library
        </NavLink>
        <NavLink to="/m/reels" className={({ isActive }) => linkCls(isActive)} data-testid="mobile-tab-reels">
          <Film size={18} />
          Reels
        </NavLink>
      </nav>
    </div>
  );
}
